import { Box, Link, Stack, styled } from '@mui/material'
import { InfoBox, tokens } from 'ComponentLibrary'
import { Typography } from 'ComponentLibrary/src/piazza/text'
import { Headline } from 'ComponentLibrary/src/piazza/text/Headline/Headline'
import {
    ArrowsLeftRightIcon,
    AtIcon,
    ChatsCircleIcon,
    EnvelopeIcon,
    FolderIcon,
    PaperclipIcon,
    PaperPlaneTiltIcon,
    TrayArrowDownIcon,
    UserCircleIcon,
    UserFocusIcon,
    WarningIcon,
} from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'

type ExternalAppraiserStepOneProps = {
    hasAsset: boolean
}

export function ExternalAppraiserStepOne({ hasAsset }: ExternalAppraiserStepOneProps) {
    const { t } = useTranslation('pages', { keyPrefix: 'orders.orderDetailsPage.externalAppraiser.intro' })
    const { t: tTerminology } = useTranslation('terminology', { keyPrefix: 'common' })

    return (
        <Stack direction="column" gap={hasAsset ? tokens.SizingSpacing400 : tokens.SizingSpacing600}>
            <FlowDiagram>
                <Stack direction="row" alignItems="center" gap={tokens.SizingSpacing100} flex={1}>
                    <Stack direction="column" gap={tokens.SizingSpacing025} alignItems="center" flex={1}>
                        <Stack direction="row" alignItems="center" gap={tokens.SizingSpacing050}>
                            <UserFocusIcon size={tokens.SizingIconS} weight="bold" color={tokens.ColorGraphSequentialThreeDarkest} />
                            <Typography variant="headline4" color={tokens.ColorGraphSequentialThreeDarkest}>
                                {t('external')}
                            </Typography>
                        </Stack>
                        <Typography variant="headline5" color={tokens.ColorGraphSequentialThreeDark}>
                            {t('viaEmail')}
                        </Typography>
                    </Stack>
                    <Stack direction="column" gap={tokens.SizingSpacing050}>
                        <FlowIconBox>
                            <EnvelopeIcon size={tokens.SizingIconS} weight="bold" color={tokens.ColorGraphSequentialThreeDark} />
                        </FlowIconBox>
                        <FlowIconBox>
                            <PaperclipIcon size={tokens.SizingIconS} weight="bold" color={tokens.ColorGraphSequentialThreeDark} />
                        </FlowIconBox>
                    </Stack>
                </Stack>

                <ArrowsLeftRightIcon size={tokens.SizingIconS} weight="bold" color={tokens.ColorIconSubtlest} />

                <FlowMailChip>
                    <AtIcon size={tokens.SizingIconS} weight="bold" color={tokens.ColorGraphSequentialThreeDarkest} />
                    <Typography variant="headline5" color={tokens.ColorGraphSequentialThreeDarkest}>
                        {t('orderEmail')}
                    </Typography>
                </FlowMailChip>

                <ArrowsLeftRightIcon size={tokens.SizingIconS} weight="bold" color={tokens.ColorIconSubtlest} />

                <Stack direction="row" alignItems="center" gap={tokens.SizingSpacing100} flex={1}>
                    <Stack direction="column" gap={tokens.SizingSpacing050}>
                        <FlowIconBox>
                            <ChatsCircleIcon size={tokens.SizingIconS} weight="bold" color={tokens.ColorGraphSequentialThreeDark} />
                        </FlowIconBox>
                        <FlowIconBox>
                            <FolderIcon size={tokens.SizingIconS} weight="bold" color={tokens.ColorGraphSequentialThreeDark} />
                        </FlowIconBox>
                    </Stack>
                    <Stack direction="column" gap={tokens.SizingSpacing025} alignItems="center" flex={1}>
                        <Stack direction="row" alignItems="center" gap={tokens.SizingSpacing050}>
                            <UserCircleIcon size={tokens.SizingIconS} weight="bold" color={tokens.ColorGraphSequentialThreeDarkest} />
                            <Typography variant="headline4" color={tokens.ColorGraphSequentialThreeDarkest}>
                                {t('user')}
                            </Typography>
                        </Stack>
                        <Typography variant="headline5" color={tokens.ColorGraphSequentialThreeDark}>
                            {t('onPlatform')}
                        </Typography>
                    </Stack>
                </Stack>
            </FlowDiagram>

            <Stack direction="column" gap={tokens.SizingSpacing150}>
                <Headline variant="headline3" title={t('title')} />
                <Typography variant="textS" color={tokens.ColorTextDefault}>
                    {t('description')}
                </Typography>
                <Stack direction="row" gap={tokens.SizingSpacing075}>
                    <AtIcon size={tokens.SizingIconS} weight="bold" color={tokens.ColorIconDefault} style={{ flexShrink: 0 }} />
                    <Typography variant="textS" color={tokens.ColorTextDefault}>
                        {t('orderEmailPrefix')}{' '}
                        <span style={{ color: tokens.ColorTextBold, fontWeight: 500 }}>{t('orderEmailHighlight')}</span>
                    </Typography>
                </Stack>
                <Stack direction="row" gap={tokens.SizingSpacing075}>
                    <TrayArrowDownIcon size={tokens.SizingIconS} weight="bold" color={tokens.ColorIconDefault} style={{ flexShrink: 0 }} />
                    <Typography variant="textS" color={tokens.ColorTextDefault}>
                        <span style={{ color: tokens.ColorTextBold, fontWeight: 500 }}>{t('incomingHighlight')}</span> {t('incomingText')}
                    </Typography>
                </Stack>
                <Stack direction="row" gap={tokens.SizingSpacing075}>
                    <PaperPlaneTiltIcon size={tokens.SizingIconS} weight="bold" color={tokens.ColorIconDefault} style={{ flexShrink: 0 }} />
                    <Typography variant="textS" color={tokens.ColorTextDefault}>
                        <span style={{ color: tokens.ColorTextBold, fontWeight: 500 }}>{t('outgoingHighlight')}</span> {t('outgoingText')}
                    </Typography>
                </Stack>
            </Stack>
            {!hasAsset && (
                <InfoBox
                    variant="warning"
                    icon={<WarningIcon />}
                    showCloseButton
                    closeButtonLabel={tTerminology('actions.close')}
                    alignItems="flex-start"
                >
                    <Stack direction="column" gap={tokens.SizingSpacing100} minWidth={0}>
                        <Typography variant="headline3" color={tokens.ColorTextBoldest}>
                            {t('missingAsset.title')}
                        </Typography>
                        <Typography variant="textS" color={tokens.ColorTextBold}>
                            {t('missingAsset.descriptionBeforeEmail')}{' '}
                            <Link href="mailto:support@einwert.com" color={tokens.ColorTextLinkDefault}>
                                support@einwert.com
                            </Link>
                            {t('missingAsset.descriptionAfterEmail')}
                        </Typography>
                    </Stack>
                </InfoBox>
            )}
        </Stack>
    )
}

const FlowDiagram = styled(Box)(() => ({
    height: '159px',
    borderRadius: tokens.SizingCornerS,
    backgroundColor: tokens.ColorGraphSequentialThreeLightest,
    padding: tokens.SizingSpacing150,
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto auto auto minmax(0, 1fr)',
    alignItems: 'center',
    columnGap: tokens.SizingSpacing200,
}))

const FlowIconBox = styled(Box)(() => ({
    width: tokens.SizingIconM,
    height: tokens.SizingIconM,
    borderRadius: tokens.SizingCornerS,
    backgroundColor: tokens.ColorGraphSequentialThreeLight,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
}))

const FlowMailChip = styled(Stack)(() => ({
    gap: tokens.SizingSpacing050,
    flexDirection: 'row',
    alignItems: 'center',
    padding: tokens.SizingSpacing075,
    borderRadius: tokens.SizingCornerS,
    border: `1px solid ${tokens.ColorGraphSequentialThreeLight}`,
    backgroundColor: tokens.ColorGraphSequentialThreeLight,
}))
